import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Teacher } from '@/interface/types/user'
import { useState, useEffect } from 'react'
import { Eye, EyeOff, Check, ChevronsUpDown } from 'lucide-react'
import { FormYearMonthPicker } from '@/components/ui/form-year-month-picker'
import FileUploader from '@/components/shared/FileUploader'
import CdnImage from '@/components/shared/CdnImage'
import { DEFAULT_AVATAR } from '@/constants'
import { useTranslation } from 'react-i18next'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

// Helper function to get all nationality keys
const getAllNationalityKeys = (): string[] => {
  return [
    'afghan', 'albanian', 'algerian', 'american', 'andorran', 'angolan', 'antiguans',
    'argentine', 'armenian', 'australian', 'austrian', 'azerbaijani', 'bahamian',
    'bahraini', 'bangladeshi', 'barbadian', 'barbudans', 'batswana', 'belarusian',
    'belgian', 'belizean', 'beninese', 'bhutanese', 'bolivian', 'bosnian', 'brazilian',
    'british', 'bruneian', 'bulgarian', 'burkinabe', 'burmese', 'burundian', 'cambodian',
    'cameroonian', 'canadian', 'cape verdean', 'central african', 'chadian', 'chilean',
    'chinese', 'colombian', 'comoran', 'congolese', 'costa rican', 'croatian', 'cuban',
    'cypriot', 'czech', 'danish', 'djibouti', 'dominican', 'dutch', 'east timorese',
    'ecuadorean', 'egyptian', 'emirian', 'equatorial guinean', 'eritrean', 'estonian',
    'ethiopian', 'fijian', 'filipino', 'finnish', 'french', 'gabonese', 'gambian',
    'georgian', 'german', 'ghanaian', 'greek', 'grenadian', 'guatemalan', 'guinea-bissauan',
    'guinean', 'guyanese', 'haitian', 'herzegovinian', 'honduran', 'hungarian', 'icelander',
    'indian', 'indonesian', 'iranian', 'iraqi', 'irish', 'israeli', 'italian', 'ivorian',
    'jamaican', 'japanese', 'jordanian', 'kazakhstani', 'kenyan', 'kittian and nevisian',
    'kuwaiti', 'kyrgyz', 'laotian', 'latvian', 'lebanese', 'liberian', 'libyan',
    'liechtensteiner', 'lithuanian', 'luxembourger', 'macedonian', 'malagasy', 'malawian',
    'malaysian', 'maldivan', 'malian', 'maltese', 'marshallese', 'mauritanian', 'mauritian',
    'mexican', 'micronesian', 'moldovan', 'monacan', 'mongolian', 'moroccan', 'mosotho',
    'motswana', 'mozambican', 'namibian', 'nauruan', 'nepalese', 'new zealander', 'ni-vanuatu',
    'nicaraguan', 'nigerien', 'north korean', 'northern irish', 'norwegian', 'omani',
    'pakistani', 'palauan', 'panamanian', 'papua new guinean', 'paraguayan', 'peruvian',
    'polish', 'portuguese', 'qatari', 'romanian', 'russian', 'rwandan', 'saint lucian',
    'salvadoran', 'samoan', 'san marinese', 'sao tomean', 'saudi', 'scottish', 'senegalese',
    'serbian', 'seychellois', 'sierra leonean', 'singaporean', 'slovakian', 'slovenian',
    'solomon islander', 'somali', 'south african', 'south korean', 'spanish', 'sri lankan',
    'sudanese', 'surinamer', 'swazi', 'swedish', 'swiss', 'syrian', 'taiwanese', 'tajik',
    'tanzanian', 'thai', 'togolese', 'tongan', 'trinidadian or tobagonian', 'tunisian',
    'turkish', 'tuvaluan', 'ugandan', 'ukrainian', 'uruguayan', 'uzbekistani', 'venezuelan',
    'vietnamese', 'welsh', 'yemenite', 'zambian', 'zimbabwean'
  ]
}

const teacherSchema = z.object({
  cin: z.string().length(8, 'CIN must be exactly 8 digits').regex(/^[0-9]{8}$/, 'CIN must contain only numbers'),
  firstname: z.string().min(2, 'First name must be at least 2 characters'),
  lastname: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email format'),
  password: z
    .string()
    .min(6, 'Password must be at least 6 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(
      /[!@#$%^&*(),.?":{}|<>]/,
      'Password must contain at least one special character'
    )
    .optional(),
  title: z.string().min(1, 'Title is required'),
  subjectIds: z.array(z.string()).min(1, 'At least one subject is required'),
  birthday: z.date().optional(),
  gender: z.enum(['male', 'female'] as const),
  address: z.string().optional(),
  phone: z.string().optional(),
  nationality: z.string().optional(),
  avatar: z.string().url().default(DEFAULT_AVATAR),
  isActive: z.boolean().default(true),
})

type TeacherFormValues = z.infer<typeof teacherSchema>
export type { TeacherFormValues }

interface TeacherFormProps {
  initialData?: Partial<Teacher>
  subjects: Array<{ id: string; name: string }>
  onSubmit: (data: TeacherFormValues) => Promise<void>
  onCancel: () => void
  submitLabel: string
}

export function TeacherForm({
  initialData,
  subjects,
  onSubmit,
  onCancel,
  submitLabel,
  onRefreshSubjects,
}: TeacherFormProps & { onRefreshSubjects?: () => void }) {
  // State for password visibility toggle
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  
  // Helper function to normalize nationality key (lowercase)
  const normalizeNationalityKey = (nationality: string | undefined): string => {
    if (!nationality) return 'tunisian'
    return nationality.toLowerCase()
  }
  
  // Nationality search state - initialize with initialData if available
  const [nationalitySearchOpen, setNationalitySearchOpen] = useState(false)
  const [nationalitySearchQuery, setNationalitySearchQuery] = useState('')
  const [selectedNationality, setSelectedNationality] = useState<string>(
    normalizeNationalityKey(initialData?.nationality)
  )

  // Function to toggle password visibility
  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev)
  }

  // Calculate date 5 years ago for default and maxDate
  const getFiveYearsAgo = () => {
    const date = new Date()
    date.setFullYear(date.getFullYear() - 5)
    return date
  }
  // Set maxDate to 5 years ago, but allow existing birthdays that are less than 5 years ago
  // (for existing users who were created before this validation)
  const fiveYearsAgo = getFiveYearsAgo()
  const existingBirthday = initialData?.birthday ? new Date(initialData.birthday) : null
  const maxBirthdayDate = existingBirthday && existingBirthday > fiveYearsAgo 
    ? existingBirthday 
    : fiveYearsAgo

  // Convert string date to Date object for the form
  const getBirthdayDate = () => {
    if (initialData?.birthday) {
      return new Date(initialData.birthday)
    }
    return getFiveYearsAgo()
  }

  // Normalize title value to match form values
  const normalizeTitle = (title: string | undefined): string => {
    if (!title) return ''
    const normalized = title.toLowerCase().trim()
    // Map various title formats to form values
    if (normalized === 'dr' || normalized === 'dr.' || normalized === 'doctor') {
      return 'dr'
    }
    if (normalized === 'professor' || normalized === 'prof') {
      return 'professor'
    }
    if (normalized === 'assistantprofessor' || normalized === 'assistant professor') {
      return 'assistantProfessor'
    }
    // If it doesn't match standard values, return as-is (for custom titles)
    return title
  }

  // Check if title is a standard option
  const isStandardTitle = (title: string | undefined): boolean => {
    if (!title) return false
    const normalized = normalizeTitle(title)
    return ['professor', 'dr', 'assistantProfessor'].includes(normalized)
  }

  const { t } = useTranslation('admin')
  const { t: tNationalities } = useTranslation('nationalities')

  // Log initial data for debugging
  // console.log(t('teacher-folder.teacherForm.debug.initialData'), initialData)
  // console.log(t('teacher-folder.teacherForm.debug.subjects'), subjects)

  const form = useForm({
    defaultValues: {
      cin: initialData?.cin || '',
      firstname: initialData?.firstname || '',
      lastname: initialData?.lastname || '',
      email: initialData?.email || '',
      password: '',
      title: normalizeTitle(initialData?.title),
      avatar: initialData?.avatar || DEFAULT_AVATAR,
      // Extract subject IDs from the subjects array if available
      subjectIds:
        initialData?.subjects?.map((subject) => {
          // Handle different types of subject data
          if (typeof subject === 'string') return subject
          if (typeof subject === 'object' && subject !== null) {
            // Handle case where subject is an object with id property
            return (subject as any).id || subject
          }
          return subject
        }) || [],
      birthday: getBirthdayDate(),
      gender:
        (initialData?.gender?.toLowerCase() as 'male' | 'female') || 'male',
      address: initialData?.address || '',
      phone: initialData?.phone || '',
      nationality: normalizeNationalityKey(initialData?.nationality),
      isActive: initialData?.isActive !== undefined ? initialData.isActive : true,
    },
    onSubmit: async ({ value }) => {
      const formData = {
        ...value,
        isActive: value.isActive !== undefined ? value.isActive : true,
        // Ensure avatar is always present
        avatar: value.avatar || DEFAULT_AVATAR,
        // Use selected nationality if available
        nationality: selectedNationality || value.nationality || 'tunisian',
      }
      await onSubmit(formData)
    },
  })

  // Sync selected nationality when initialData changes (after form is created)
  useEffect(() => {
    const nationality = normalizeNationalityKey(initialData?.nationality)
    setSelectedNationality(nationality)
    // Also update the form field value
    form.setFieldValue('nationality', nationality)
  }, [initialData?.nationality, form])
  
  // Sync field value when selectedNationality changes
  useEffect(() => {
    if (selectedNationality) {
      form.setFieldValue('nationality', selectedNationality)
    }
  }, [selectedNationality, form])

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
      className="space-y-6"
    >
      <div className="grid grid-cols-2 gap-4">
        <form.Field
          name="cin"
          validators={{
            onChange: ({ value }) => {
              if (!value)
                return t(
                  'teacher-folder.teacherForm.fields.cin.validation.required'
                )
              if (value.length !== 8)
                return t('teacher-folder.teacherForm.fields.cin.validation.minLength') || 'CIN must be exactly 8 digits'
              if (!/^[0-9]{8}$/.test(value))
                return 'CIN must contain only numbers'
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="cin">{t('teacher-folder.teacherForm.fields.cin.label')}</Label>
              <Input
                id="cin"
                type="text"
                inputMode="numeric"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => {
                  // Only allow numbers
                  const value = e.target.value.replace(/[^0-9]/g, '')
                  // Limit to 8 characters
                  const limitedValue = value.slice(0, 8)
                  field.handleChange(limitedValue)
                }}
                maxLength={8}
                minLength={8}
                pattern="[0-9]{8}"
                title="CIN must be exactly 8 digits"
                placeholder={t('teacher-folder.teacherForm.fields.cin.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="firstname"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('teacher-folder.teacherForm.fields.firstname.validation.required')
              if (value.length < 2)
                return t('teacher-folder.teacherForm.fields.firstname.validation.minLength')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="firstname">
                {t('teacher-folder.teacherForm.fields.firstname.label')}
              </Label>
              <Input
                id="firstname"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('teacher-folder.teacherForm.fields.firstname.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="lastname"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('teacher-folder.teacherForm.fields.lastname.validation.required')
              if (value.length < 2) return t('teacher-folder.teacherForm.fields.lastname.validation.minLength')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="lastname">
                {t('teacher-folder.teacherForm.fields.lastname.label')}
              </Label>
              <Input
                id="lastname"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('teacher-folder.teacherForm.fields.lastname.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="email"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('teacher-folder.teacherForm.fields.email.validation.required')
              if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
                return t('teacher-folder.teacherForm.fields.email.validation.format')
              }
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="email">
                {t('teacher-folder.teacherForm.fields.email.label')}
              </Label>
              <Input
                id="email"
                type="email"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('teacher-folder.teacherForm.fields.email.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        {!initialData ? (
          <form.Field
            name="password"
            validators={{
              onChange: ({ value }) => {
                if (!value) return t('teacher-folder.teacherForm.fields.password.validation.required')
                if (value.length < 6) return t('teacher-folder.teacherForm.fields.password.validation.minLength')
                if (!/[A-Z]/.test(value)) return t('teacher-folder.teacherForm.fields.password.validation.uppercase')
                if (!/[0-9]/.test(value)) return t('teacher-folder.teacherForm.fields.password.validation.number')
                if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return t('teacher-folder.teacherForm.fields.password.validation.special')
                return undefined
              },
            }}
          >
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="password">
                  {t('teacher-folder.teacherForm.fields.password.label')}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={isPasswordVisible ? 'text' : 'password'}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('teacher-folder.teacherForm.fields.password.placeholder')}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-2 flex items-center"
                    tabIndex={-1}
                    onClick={togglePasswordVisibility}
                  >
                    {isPasswordVisible ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {field.state.meta.errors && (
                  <p className="text-sm text-red-500">
                    {field.state.meta.errors.join(', ')}
                  </p>
                )}
              </div>
            )}
          </form.Field>
        ) : (
          <form.Field
            name="password"
            validators={{
              onBlur: ({ value }) => {
                // Skip validation if empty (keeping current password)
                if (!value || value.trim() === '') return undefined

                // Validate password only if provided
                const errors = []
                if (value.length < 6) {
                  errors.push(t('teacher-folder.teacherForm.fields.password.validation.minLength'))
                }
                if (!/[A-Z]/.test(value)) {
                  errors.push(t('teacher-folder.teacherForm.fields.password.validation.uppercase'))
                }
                if (!/[0-9]/.test(value)) {
                  errors.push(t('teacher-folder.teacherForm.fields.password.validation.number'))
                }
                if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
                  errors.push(t('teacher-folder.teacherForm.fields.password.validation.special'))
                }

                return errors.length > 0 ? errors.join(', ') : undefined
              },
            }}
          >
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="password">
                  {t('teacher-folder.teacherForm.fields.password.empty_change')}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={isPasswordVisible ? 'text' : 'password'}
                    value={field.state.value}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => {
                      field.handleBlur()
                      setIsPasswordFocused(false)
                    }}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('teacher-folder.teacherForm.fields.password.placeholder')}
                    className={
                      field.state.meta.isTouched && field.state.meta.errors
                        ? 'border-red-500'
                        : ''
                    }
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-2 flex items-center"
                    tabIndex={-1}
                    onClick={togglePasswordVisibility}
                  >
                    {isPasswordVisible ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {field.state.meta.errors && (
                  <p className="text-sm text-red-500">
                    {field.state.meta.errors.join(', ')}
                  </p>
                )}
                {isPasswordFocused && (
                  <p className="text-xs text-gray-500">
                    {t(
                      'teacher-folder.teacherForm.fields.password.empty_change'
                    )}
                  </p>
                )}
              </div>
            )}
          </form.Field>
        )}

        <form.Field
          name="title"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('teacher-folder.teacherForm.fields.title.validation.required')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="title">
                {t('teacher-folder.teacherForm.fields.title.label')}
              </Label>
              <Select
                value={field.state.value}
                onValueChange={field.handleChange}
              >
                <SelectTrigger id="title">
                  <SelectValue placeholder={t('teacher-folder.teacherForm.fields.title.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professor">
                    {t('teacher-folder.teacherForm.fields.title.options.professor')}
                  </SelectItem>
                  <SelectItem value="dr">
                    {t('teacher-folder.teacherForm.fields.title.options.dr')}
                  </SelectItem>
                  <SelectItem value="assistantProfessor">
                    {t('teacher-folder.teacherForm.fields.title.options.assistantProfessor')}
                  </SelectItem>
                  {/* Add the current title if it's not a standard option */}
                  {initialData?.title &&
                    !isStandardTitle(initialData.title) && (
                      <SelectItem key={`custom-${initialData.title}`} value={initialData.title}>
                        {initialData.title}
                      </SelectItem>
                    )}
                </SelectContent>
              </Select>
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="subjectIds"
          validators={{
            onChange: ({ value }) => {
              // Allow empty arrays for subjects, but validate if subjects are required
              if (value.length === 0) {
                return t('teacher-folder.teacherForm.fields.subjects.validation.required')
              }
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="subjects">
                  {t('teacher-folder.teacherForm.fields.subjects.label')}
                </Label>
                {onRefreshSubjects && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault()
                      onRefreshSubjects()
                    }}
                    className="h-8 px-2 text-xs"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-1"
                    >
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                      <path d="M21 3v5h-5" />
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                      <path d="M3 21v-5h5" />
                    </svg>
                    {t('teacher-folder.teacherForm.fields.subjects.refreshButton')}
                  </Button>
                )}
              </div>
              <Select
                value={field.state.value[field.state.value.length - 1] || ''}
                onValueChange={(value) => {
                  if (!field.state.value.includes(value)) {
                    field.handleChange([...field.state.value, value])
                  }
                }}
              >
                <SelectTrigger id="subjects">
                  <SelectValue
                    placeholder={t(
                      'teacher-folder.teacherForm.fields.subjects.placeholder'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {subjects.length === 0 ? (
                    <div className="p-2 text-center text-sm text-gray-500">
                      {t(
                        'teacher-folder.teacherForm.fields.subjects.noSubjects'
                      )}
                      {onRefreshSubjects && (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault()
                            onRefreshSubjects()
                          }}
                          className="px-1"
                        >
                          {t('teacher-folder.teacherForm.buttons.refresh')}
                        </Button>
                      )}
                    </div>
                  ) : (
                    subjects.map((subject) => (
                      <SelectItem key={subject.id} value={subject.id}>
                        {subject.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <div className="mt-2 flex flex-wrap gap-2">
                {field.state.value.map((subjectId) => {
                  const subject = subjects.find((s) => s.id === subjectId)
                  if (!subject) return null
                  return (
                    <Button
                      key={subjectId}
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault()
                        field.handleChange(
                          field.state.value.filter((id) => id !== subjectId)
                        )
                      }}
                    >
                      {subject.name} ×
                    </Button>
                  )
                })}
              </div>
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field 
          name="birthday"
          validators={{
            onChange: ({ value }) => {
              if (!value) return undefined // Optional field
              
              const today = new Date()
              const birthDate = new Date(value)
              let age = today.getFullYear() - birthDate.getFullYear()
              const monthDiff = today.getMonth() - birthDate.getMonth()
              
              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--
              }
              
              if (age < 5) {
                return t('teacher-folder.teacherForm.fields.birthday.validation.minAge')
              }
              
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <FormYearMonthPicker
                id="birthday"
                label={t('teacher-folder.teacherForm.fields.birthday.label')}
                value={field.state.value}
                onChange={(date) => field.handleChange(date || getFiveYearsAgo())}
                onBlur={field.handleBlur}
                placeholder="Select birthday"
                maxDate={maxBirthdayDate}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="gender"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('teacher-folder.teacherForm.fields.gender.validation.required')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="gender">
                {t('teacher-folder.teacherForm.fields.gender.label')}
              </Label>
              <Select
                value={field.state.value}
                onValueChange={(value: 'male' | 'female') =>
                  field.handleChange(value)
                }
              >
                <SelectTrigger id="gender">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">
                    {t('teacher-folder.teacherForm.fields.gender.options.male')}
                  </SelectItem>
                  <SelectItem value="female">
                    {t(
                      'teacher-folder.teacherForm.fields.gender.options.female'
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="address">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="address">
                {t('teacher-folder.teacherForm.fields.address.label')}
              </Label>
              <Input
                id="address"
                value={field.state.value || ''}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('teacher-folder.teacherForm.fields.address.placeholder')}
              />
            </div>
          )}
        </form.Field>

        <form.Field name="phone">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="phone">
                {t('teacher-folder.teacherForm.fields.phone.label')}
              </Label>
              <Input
                id="phone"
                type="tel"
                value={field.state.value || ''}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('teacher-folder.teacherForm.fields.phone.placeholder')}
              />
            </div>
          )}
        </form.Field>

        <form.Field name="nationality">
          {(field) => {
            // Use selectedNationality as primary source, fallback to field value, then initialData, then default
            // Normalize all values to lowercase for comparison
            const normalizedFieldValue = field.state.value ? normalizeNationalityKey(field.state.value) : null
            const normalizedInitialData = initialData?.nationality ? normalizeNationalityKey(initialData.nationality) : null
            const currentNationality = selectedNationality || normalizedFieldValue || normalizedInitialData || 'tunisian'
            
            return (
              <div className="space-y-2">
                <Label htmlFor="nationality">
                  {t('teacher-folder.teacherForm.fields.nationality.label')}
                </Label>
                <Popover open={nationalitySearchOpen} onOpenChange={setNationalitySearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={nationalitySearchOpen}
                      className="w-full justify-between"
                      type="button"
                    >
                      {(() => {
                        if (getAllNationalityKeys().includes(currentNationality)) {
                          return tNationalities(`nationalities.${currentNationality}`, currentNationality)
                        }
                        return t('teacher-folder.teacherForm.fields.nationality.placeholder')
                      })()}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <div className="p-2">
                      <Input
                        placeholder={t('teacher-folder.teacherForm.fields.nationality.placeholder')}
                        value={nationalitySearchQuery}
                        onChange={(e) => setNationalitySearchQuery(e.target.value)}
                        className="mb-2"
                      />
                    </div>
                    <ScrollArea className="h-[200px]">
                      <div className="p-1">
                        {getAllNationalityKeys()
                          .filter((key) => {
                            if (!nationalitySearchQuery) return true
                            const translated = tNationalities(`nationalities.${key}`, key).toLowerCase()
                            const searchLower = nationalitySearchQuery.toLowerCase()
                            return translated.includes(searchLower) || key.toLowerCase().includes(searchLower)
                          })
                          .map((key) => {
                            const translated = tNationalities(`nationalities.${key}`, key)
                            const isSelected = currentNationality === key
                            return (
                              <button
                                key={key}
                                type="button"
                                className={cn(
                                  'w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer',
                                  isSelected && 'bg-accent'
                                )}
                                onClick={() => {
                                  setSelectedNationality(key)
                                  field.handleChange(key)
                                  setNationalitySearchQuery('')
                                  setNationalitySearchOpen(false)
                                }}
                              >
                                <span>{translated}</span>
                                {isSelected && <Check className="h-4 w-4" />}
                              </button>
                            )
                          })}
                        {nationalitySearchQuery && getAllNationalityKeys().filter((key) => {
                          const translated = tNationalities(`nationalities.${key}`, key).toLowerCase()
                          const searchLower = nationalitySearchQuery.toLowerCase()
                          return translated.includes(searchLower) || key.toLowerCase().includes(searchLower)
                        }).length === 0 && (
                          <div className="px-2 py-1.5 text-sm text-gray-500">
                            No nationality found
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              </div>
            )
          }}
        </form.Field>

        <form.Field name="avatar">
          {(field) => (
            <FileUploader
              label={t('teacher-folder.teacherForm.fields.avatar.label')}
              defaultPreview={field.state.value || DEFAULT_AVATAR}
              onFileUploaded={(url) => field.handleChange(url)}
              isAvatar={true}
              previewComponent={(previewUrl) => (
                <CdnImage
                  src={previewUrl}
                  alt="Teacher avatar"
                  className="h-28 w-28 rounded-full object-cover border-2 border-gray-200 shadow-sm"
                />
              )}
            />
          )}
        </form.Field>
      </div>

      <div className="flex justify-end gap-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('teacher-folder.teacherForm.buttons.cancel')}
        </Button>
        <Button type="submit">{submitLabel}</Button>
      </div>
    </form>
  )
}
